import React, { forwardRef, useRef, useState } from 'react';

import { alpha, Menu, MenuItem, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { LEFT_NAV_BACKGROUND_COLOR, LeftNavIcon } from 'src/components';
import { MoreHorizIcon } from 'src/components/icons/MoreHorizIcon';
import { makeStyles } from 'tss-react/mui';

import { useLeftNavItemStyles } from './LeftNavItem';
import { NavItemText } from './NavItemText';
import type { LeftNavSection } from '../ui-interfaces';
import { getRouteForAppSection } from '../ui-interfaces';

const useStyles = makeStyles()(theme => ({
  menu: {
    '& .MuiPaper-root': {
      backgroundColor: LEFT_NAV_BACKGROUND_COLOR,
      borderRadius: 8,
      marginLeft: theme.spacing(1),
    },
  },
  menuItem: {
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.1),
    },
    alignItems: 'center',
    color: theme.palette.common.white,
    display: 'flex',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
  },
  menuItemLabel: {
    color: theme.palette.common.white,
    fontSize: 14,
  },
}));

interface MoreNavButtonProps {
  selected: boolean;
  onClick?: () => void;
}

export const MoreNavButton = forwardRef<HTMLDivElement, MoreNavButtonProps>(function MoreNavButton(
  { onClick, selected },
  ref
): JSX.Element {
  const { classes: navItemClasses } = useLeftNavItemStyles({ selected });

  return (
    <div ref={ref} className={navItemClasses.content} onClick={onClick}>
      <MoreHorizIcon />
      <NavItemText>More</NavItemText>
    </div>
  );
});

interface MoreNavMenuProps {
  sections: LeftNavSection[];
  selectedNodeId: string;
}

export function MoreNavMenu({ sections, selectedNodeId }: MoreNavMenuProps): JSX.Element | null {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const isAnyOverflowSelected = sections.some(
    section => getRouteForAppSection(section) === selectedNodeId
  );

  if (sections.length === 0) return null;

  return (
    <>
      <MoreNavButton
        ref={buttonRef}
        selected={isAnyOverflowSelected || menuOpen}
        onClick={() => setMenuOpen(!menuOpen)}
      />
      <Menu
        anchorEl={buttonRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'center' }}
        className={classes.menu}
        open={menuOpen}
        transformOrigin={{ horizontal: 'left', vertical: 'center' }}
        onClose={() => setMenuOpen(false)}
      >
        {sections.map(section => {
          const path = getRouteForAppSection(section);
          const isSelected = selectedNodeId === path;

          return (
            <MenuItem
              key={section}
              className={classes.menuItem}
              sx={theme => ({
                backgroundColor: isSelected ? alpha(theme.palette.common.white, 0.1) : undefined,
              })}
              onClick={() => {
                navigate(path);
                setMenuOpen(false);
              }}
            >
              <LeftNavIcon section={section} selected={isSelected} />
              <Typography className={classes.menuItemLabel}>{section}</Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
